import { appVersion } from '@mononoke/shared';
import { useEffect, useRef, useState } from 'react';
import {
  authConfigured,
  authRedirectError,
  authSessionAdoptedFromUrl,
  signInWithPassword,
  signOut,
  signUp,
  type AuthState,
} from '../platform/auth.js';
import { api, call } from '../platform/api.js';
import { redeemInvitation } from '../platform/join.js';

/**
 * ACCEPTING AN INVITATION — `docs/self-service-accounts.md` §4, stage 2.
 *
 * ⚠️ THE ONLY SCREEN IN THE APP A STRANGER IS MEANT TO REACH. Everything else assumes an
 * atelier user with a seat; this one assumes somebody who has neither and may not even have an
 * account yet. It renders OUTSIDE the signed-in shell — no nav, no queue badge — for the same
 * reason the deleted client view did: the surrounding furniture is meaningless to them, and a
 * sign-in form they cannot use is an invitation to try it.
 *
 * ⚠️ IT TAKES TWO VISITS, AND THAT IS THE DESIGN RATHER THAN A LIMITATION. §2 requires
 * Supabase's `Confirm email` to stay ON — without it somebody can sign up using an invitee's
 * address before the invitee does and squat the account. With it on, `signUp` returns a user
 * and NO session, so nobody can create a password and redeem in one sitting. Ruled by the
 * maintainer, 9 Aug 2026: the invitee confirms their address and comes back to the SAME link,
 * which is in the invitation email they already have. The alternative — a Supabase redirect
 * back to `/join?token=…` — needs that URL on the project's Redirect-URLs allow-list, a console
 * change, and is written up as stage 3's option rather than assumed here.
 *
 * ⚠️ IT NEVER SHOWS THE INVITED ADDRESS — §9 B, ruled 8 Aug 2026. The invitee types their own;
 * a mismatch is refused by the server against the JWT's `email` claim. Displaying it would turn
 * the token into an oracle: anybody holding a link could learn who it was for. One extra field
 * is the whole cost.
 */

/** The three things this screen can be doing, and they are three different screens to a reader. */
type Mode = 'create' | 'signin';

/**
 * THE SIGNED-IN HALF, and which button it offers depends on one question: does this account
 * already hold a seat? Seated → **Sign out is the only act that can help**, so Join is not
 * rendered at all (maintainer, 10 Aug 2026 — a button whose every outcome is a refusal is a
 * trap, not an option). No seat → this is the invitee's second visit, and Join is the whole
 * point. Unknown (network trouble) → Join stays, because hiding it from a real invitee on a
 * flaky connection strands them, while showing it to a seated account costs one refused click.
 */
function JoinSignedIn({
  auth,
  busy,
  problem,
  onJoin,
}: {
  auth: Extract<AuthState, { status: 'signed-in' }>;
  busy: boolean;
  problem: string | null;
  onJoin: () => Promise<void>;
}): JSX.Element {
  const [seat, setSeat] = useState<'unknown' | 'seated' | 'none'>('unknown');
  useEffect(() => {
    let live = true;
    void (async () => {
      const result = await call('whoami', () => api.whoami.query());
      if (!live) return;
      // Only a CLEAN refusal means seatless — the same rule the shell's access gate follows.
      setSeat(result.ok ? 'seated' : result.failure.kind === 'unauthenticated' ? 'none' : 'unknown');
    })();
    return () => {
      live = false;
    };
  }, []);

  return (
    <section className="auth-card">
      <h1 className="auth-brand">Mononoke</h1>
      <h2>Accept your invitation</h2>
      {problem !== null && <p role="alert">{problem}</p>}
      {seat === 'seated' ? (
        <>
          <p>
            You are signed in as <strong>{auth.email ?? 'your account'}</strong>, and that account is already in the
            workspace — it cannot accept an invitation.
          </p>
          {/* §9 B still holds: the invited address is never named. The reader knows which
              address their invitation went to; this screen must not confirm it. */}
          <p>
            <small>
              Your invitation was sent to one specific email address. Sign out, then create (or sign in to) the
              account for that address — this page keeps your invitation while you do.
            </small>
          </p>
          {/* Seated: Sign out is the only act that can help, so it IS the primary. Signing
              out re-renders this screen into its create-or-sign-in half WITHOUT navigating, so
              the token stays in the URL and the invitation survives the account switch. */}
          <button type="button" className="auth-primary" disabled={busy} onClick={() => void signOut()}>
            Sign out
          </button>
        </>
      ) : (
        <>
          <p>
            You are signed in as <strong>{auth.email ?? 'your account'}</strong>. Accepting adds <strong>this</strong>{' '}
            account to the workspace.
          </p>
          {/* No seat: Join is the whole point (frame 6c), with Sign out demoted to the text
              switch beside it — same account-switch escape, kept as the quiet option. */}
          <div className="auth-foot">
            <button type="button" className="auth-toggle" disabled={busy} onClick={() => void signOut()}>
              Sign out
            </button>
            <button type="button" className="auth-primary" disabled={busy} onClick={() => void onJoin()}>
              {busy ? 'Joining…' : 'Join'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export function Join({ token, auth }: { token: string | null; auth: AuthState }): JSX.Element {
  const [mode, setMode] = useState<Mode>('create');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * ⚠️ SEEDED FROM THE REDIRECT, so an expired or already-used confirmation link says so instead
   * of dumping somebody on a sign-in form with no explanation. `authRedirectError()` is read once
   * at mount because `initAuth` has already consumed and cleared the URL by then.
   */
  const [problem, setProblem] = useState<string | null>(() => authRedirectError());
  /** Set once the account exists. The person's next act is in their mail client, not here. */
  const [checkYourMail, setCheckYourMail] = useState(false);
  const [joined, setJoined] = useState(false);
  // Intent tracked separately from value: a browser that autofilled both fields has not told
  // us the person meant to submit them (standing rule).
  const [touched, setTouched] = useState(false);

  /**
   * ⚠️ THE CONFIRMATION LINK PRESSES JOIN ITSELF — the last press removed, 10 Aug 2026
   * ("make sure the sign up is as smooth and simple as possible"). This fires ONLY when the
   * session was adopted from the URL fragment this page load: that person just confirmed the
   * invited address, and redeeming is the only thing they can have come to do.
   *
   * A merely signed-in visitor still gets the explicit button — most likely an admin in the
   * wrong tab (the 9 Aug field report), and auto-firing a refusal at somebody who pressed
   * nothing would read as the app breaking on its own.
   *
   * The ref, not state: React 18 runs effects twice in StrictMode dev, and a second redeem of a
   * just-spent token would answer "already a member" over the top of "You are in".
   */
  const autoJoined = useRef(false);
  useEffect(() => {
    if (auth.status !== 'signed-in' || token === null) return;
    if (!authSessionAdoptedFromUrl() || autoJoined.current) return;
    autoJoined.current = true;
    void join();
  }, [auth.status, token]);

  /**
   * ⚠️ A MISSING TOKEN IS ITS OWN ANSWER, checked before anything else. Somebody who typed
   * `/join` by hand, or whose mail client mangled the link, must not be walked through creating
   * an account that then has nothing to redeem — that produces an orphan `auth.users` row and a
   * person who believes they have joined something.
   */
  if (token === null) {
    return (
      <section className="auth-card">
        <h1 className="auth-brand">Mononoke</h1>
        <h2>This invitation link is incomplete</h2>
        <p>Open the link from your invitation email exactly as it was sent, or ask for a new one.</p>
      </section>
    );
  }

  if (!authConfigured()) {
    return (
      <section className="auth-card">
        <h1 className="auth-brand">Mononoke</h1>
        <p role="alert">This build has no sign-in configured, so invitations cannot be accepted.</p>
      </section>
    );
  }

  async function createAccount(): Promise<void> {
    setBusy(true);
    setProblem(null);
    /**
     * ⚠️ THE CONFIRMATION LINK COMES BACK HERE, TOKEN AND ALL. `window.location.href` is this
     * exact page including `?token=…`, so confirming returns them to the invitation rather than
     * to a bare app — one press of Join and they are in, instead of hunting for the email again.
     */
    const result = await signUp(email.trim(), password, window.location.href);
    setBusy(false);
    if (!result.ok) {
      setProblem(result.message);
      /**
       * ⚠️ TAKE THEM WHERE THE MESSAGE SENDS THEM. Telling somebody to sign in and leaving them
       * on a Create-account form with the switch at the bottom is half an instruction — the
       * 10 Aug report was exactly that: *"couldn't click on sign in"*. The typed address is kept
       * (it is the right one), the password is cleared (the one they chose was never used).
       */
      if (result.accountExists === true) {
        setMode('signin');
        setPassword('');
      }
      return;
    }
    setCheckYourMail(true);
  }

  async function signInThenJoin(): Promise<void> {
    setBusy(true);
    setProblem(null);
    const result = await signInWithPassword(email.trim(), password);
    setBusy(false);
    // On success the auth state publishes and this component re-renders into the signed-in
    // branch below, where the Join button is. No navigation, so the token stays in the URL.
    if (!result.ok) setProblem(result.message);
  }

  async function join(): Promise<void> {
    setBusy(true);
    setProblem(null);
    const result = await redeemInvitation(token!);
    setBusy(false);
    if (result.ok) {
      setJoined(true);
      return;
    }
    setProblem(result.message);
  }

  if (joined) {
    return (
      <section className="auth-card">
        <h1 className="auth-brand">Mononoke</h1>
        <h2 className="auth-title-ok">You are in</h2>
        <p>Your account now has access to this workspace.</p>
        {/**
          * ⚠️ A FULL RELOAD, NOT A ROUTER NAVIGATION. The app decided at boot that this session
          * had no seat; the whole shell — nav, capabilities, whoami — is built on that answer.
          * Pushing a route would leave every one of those reads stale, and the person would
          * land on an app that still believes they are nobody.
          */}
        <button type="button" className="auth-primary" onClick={() => window.location.assign('/')}>
          Open Mononoke
        </button>
      </section>
    );
  }

  /**
   * SIGNED IN ALREADY — the second visit, and also the case where somebody who already has an
   * account was invited to a workspace. One button, because everything else is done.
   */
  if (auth.status === 'signed-in') {
    /**
     * ⚠️ A SEATED ACCOUNT GETS NO JOIN BUTTON — maintainer, 10 Aug 2026, from the walk that
     * produced the screenshot: signed in as their admin account, holding somebody else's
     * invitation, the screen offered Join, and Join can only ever answer "already part of a
     * workspace". A button whose every outcome is a refusal is not an option, it is a trap.
     *
     * `seat` is the same whoami question the shell asks, asked here for the same reason: only
     * a CLEAN `unauthenticated` means "no seat" (that caller may Join — it is the invitee's
     * second visit). `ok` means seated → sign out is the only act that can help. Anything
     * else — network down, cold API — stays `unknown`, and unknown keeps the Join button:
     * wrongly hiding it from a real invitee on a flaky connection strands them, while wrongly
     * showing it to a seated admin costs one refused click. The cheap mistake is chosen.
     */
    return <JoinSignedIn auth={auth} busy={busy} problem={problem} onJoin={join} />;
  }

  if (checkYourMail) {
    return (
      <section className="auth-card">
        <h1 className="auth-brand">Mononoke</h1>
        <h2>Check your email</h2>
        <p>
          We sent a confirmation message to the address you just used. <strong>Open it and confirm</strong> — that
          link brings you straight back here, signed in, and all that is left is to press Join.
        </p>
        {/* Said plainly because it is the step people lose. The invitation email is still in
            their inbox next to the confirmation, which is why the copy points at it rather than
            at a button this screen cannot provide. */}
        {/* The fallback, kept and demoted. If the redirect URL is not on Supabase's allow-list
            GoTrue substitutes the Site URL, and they land on the app instead of here — signed in
            with no seat, which since 0.1.213 is a screen telling them to open this link. Saying
            so here too costs one sentence and covers a console setting we cannot verify. */}
        <p>
          <small>
            If it does not bring you back, open the invitation link from your email again. Your invitation is still
            waiting either way.
          </small>
        </p>
        <button type="button" className="auth-primary" onClick={() => setMode('signin')}>
          I have confirmed — sign in
        </button>
      </section>
    );
  }

  const canSubmit = touched && email.trim() !== '' && password !== '' && !busy;
  const submit = (): void => void (mode === 'create' ? createAccount() : signInThenJoin());
  /**
   * Enter submits — the smoothness pass, 10 Aug 2026. This screen deliberately has no native
   * <form> (a native submit reloads the page and loses the boot waterfall, the same reason
   * `signin.tsx` gives), so without this the keyboard path dead-ends at the password field and
   * the person has to find the mouse on the one screen a stranger types two fields into.
   */
  const submitOnEnter = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && canSubmit) submit();
  };

  return (
    <section className="auth-card">
      <h1 className="auth-brand">Mononoke</h1>
      <p className="app-version">v{appVersion()}</p>
      <h2>{mode === 'create' ? 'Accept your invitation' : 'Sign in to accept'}</h2>
      {problem !== null && <p role="alert">{problem}</p>}
      <p>
        {mode === 'create'
          ? 'Create your account with the email address your invitation was sent to, then confirm it.'
          : 'Sign in with the account whose address the invitation was sent to.'}
      </p>
      <label>
        Email
        <input
          type="email"
          autoComplete="username"
          // The person arrived from an email link with exactly one job; the cursor should
          // already be where that job starts.
          autoFocus
          value={email}
          onKeyDown={submitOnEnter}
          onChange={(e) => {
            setTouched(true);
            setEmail(e.target.value);
          }}
        />
      </label>
      <label>
        Password
        <input
          type="password"
          // A new password on the create path and an existing one on the sign-in path: the
          // wrong hint here makes a password manager offer to overwrite a saved credential.
          autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
          value={password}
          onKeyDown={submitOnEnter}
          onChange={(e) => {
            setTouched(true);
            setPassword(e.target.value);
          }}
        />
      </label>
      {/* Frame 6a: the mode toggle (a text switch) sits on the foot beside the primary pill,
          not stacked below it — the switch is the quiet alternative to the one act. */}
      <div className="auth-foot">
        <button
          type="button"
          className="auth-toggle"
          onClick={() => {
            setMode(mode === 'create' ? 'signin' : 'create');
            setProblem(null);
          }}
        >
          {mode === 'create' ? 'I already have an account' : 'I need to create an account'}
        </button>
        <button type="button" className="auth-primary" disabled={!canSubmit} onClick={submit}>
          {busy ? 'Working…' : mode === 'create' ? 'Create account' : 'Sign in'}
        </button>
      </div>
    </section>
  );
}
